import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, X, ChevronRight } from 'lucide-react';
import { lookbookImages } from '../data';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { Link } from 'react-router-dom';

const categories = [
  { id: 'all', label: 'All Collections' },
  { id: 'rings', label: 'Rings' },
  { id: 'necklaces', label: 'Necklaces' },
  { id: 'bracelets', label: 'Bracelets' },
  { id: 'earrings', label: 'Earrings' }
];

const LookbookPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1, once: true });
  
  const filteredImages = activeCategory === 'all' 
    ? lookbookImages 
    : lookbookImages.filter(img => img.category === activeCategory);

  // Pinterest-style staggered grid layout
  const getItemStyle = (index: number) => {
    const row = Math.floor(index / 3);
    const isFirstColumn = index % 3 === 0;
    const isLastColumn = index % 3 === 2;
    
    let marginTop = 0;
    
    if (row > 0) {
      if (isFirstColumn) marginTop = -40;
      if (isLastColumn) marginTop = 40;
    }
    
    return {
      marginTop: `${marginTop}px`
    };
  };
  
  return (
    <div ref={ref} className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl mb-4">Lookbook</h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Explore our curated collection of fine jewelry pieces, each telling its own unique story. 
            From timeless classics to contemporary designs, discover the perfect expression of your style.
          </p>
          <div className="flex justify-center gap-4">
            <Link 
              to="/shop" 
              className="inline-flex items-center text-gold hover:text-gold/80 transition-colors"
            >
              Shop Collection
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
            <Link 
              to="/custom-jewelry" 
              className="inline-flex items-center text-gold hover:text-gold/80 transition-colors"
            >
              Custom Design
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </motion.div>
        
        <motion.div 
          className="flex justify-center mb-12 overflow-x-auto pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map((category, index) => (
            <motion.button
              key={category.id}
              className={`px-6 py-3 mx-2 text-sm uppercase tracking-wider transition-colors whitespace-nowrap ${
                activeCategory === category.id 
                  ? 'text-gold border-b-2 border-gold' 
                  : 'text-gray-500 hover:text-gold'
              }`}
              onClick={() => setActiveCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            >
              {category.label}
            </motion.button>
          ))}
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div 
                key={image.id} 
                className="relative group overflow-hidden rounded-lg shadow-lg"
                style={window.innerWidth > 1024 ? getItemStyle(index) : {}}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ 
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                layout
              >
                <motion.img 
                  src={image.src} 
                  alt={image.alt}
                  className="w-full h-96 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.7 }}
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-end p-6"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-white text-xl font-serif mb-2">{image.alt}</h3>
                  <p className="text-white/80 text-sm mb-4 capitalize">{image.category}</p>
                  <motion.button 
                    className="bg-white p-3 rounded-full shadow-md hover:bg-gold hover:text-white transition-colors"
                    onClick={() => setSelectedImage(image.src)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Eye size={20} />
                  </motion.button>
                </motion.div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div 
            className="relative max-w-4xl w-full md:max-h-[80vh] max-h-[90vh]"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="absolute -top-12 right-0 z-10 bg-white/10 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage}
              alt="Lookbook"
              className="w-full h-full object-contain rounded-lg"
            />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LookbookPage;
 